import { useFormikContext, FieldArray, FormikErrors } from 'formik';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';

import { GitFormUrlField } from '@/react/portainer/gitops/GitFormUrlField';
import { RefField } from '@/react/portainer/gitops/RefField';
import { AuthFieldset } from '@/react/portainer/gitops/AuthFieldset';
import { PathSelector } from '@/react/portainer/gitops/ComposePathField/PathSelector';
import { GitFormModel, AutoUpdateModel } from '@/react/portainer/gitops/types';
import { AutoUpdateFieldset } from '@/react/portainer/gitops/AutoUpdateFieldset';
import { useGitRepoValidity } from '@/react/portainer/gitops/hooks/useGitRepoValidity';
import {
  getAuthentication,
  getRepositoryPathWithoutHost,
} from '@/react/portainer/gitops/utils';
import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { baseStackWebhookUrl } from '@/portainer/helpers/webhookHelper';
import { useHelmValuesPreview } from '@/react/portainer/gitops/queries/useHelmValuesPreview';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { isErrorType, isArrayErrorType } from '@@/form-components/formikUtils';
import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';
import { FormControl } from '@@/form-components/FormControl';
import { Button } from '@@/buttons';
import { FormError } from '@@/form-components/FormError';
import { TextTip } from '@@/Tip/TextTip';
import { ExternalLink } from '@@/ExternalLink';
import { Checkbox } from '@@/form-components/Checkbox';
import { Badge } from '@@/Badge';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

import { HelmValuesInput } from '../components/HelmValuesInput';
import { useGitHelmChartValidation } from '../helmChartSourceQueries/useGitHelmChartValidation';
import { useGitHelmChartValues } from '../helmChartSourceQueries/useGitHelmChartValues';
import { useGitHelmChartInfo } from '../helmChartSourceQueries/useGitHelmChartInfo';
import { ManifestPreviewFormSection } from '../components/ManifestPreviewFormSection';
import { GitHelmDryRunPayload } from '../helmReleaseQueries/gitHelmTypes';
import { HelmRelease } from '../types';

import { UpgradeHelmFormValues } from './types';

type Props = {
  setPreviewIsValid: (isValid: boolean) => void;
  release: HelmRelease;
};

export function GitUpgradeFormSections({ setPreviewIsValid, release }: Props) {
  const environmentId = useEnvironmentId();
  const { values, errors, setFieldValue, initialValues } =
    useFormikContext<UpgradeHelmFormValues>();

  // Convert UpgradeHelmFormValues to GitFormModel for autocomplete components
  const gitModel: GitFormModel = {
    RepositoryURL: values.RepositoryURL || '',
    RepositoryURLValid: values.RepositoryURLValid,
    ComposeFilePathInRepository: '', // Not used for Helm
    RepositoryReferenceName: values.RepositoryReferenceName,
    AdditionalFiles: [], // Not used for Helm
    TLSSkipVerify: values.TLSSkipVerify,
    RepositoryAuthentication: values.RepositoryAuthentication,
    RepositoryUsername: values.RepositoryUsername,
    RepositoryPassword: values.RepositoryPassword,
    RepositoryGitCredentialID: values.RepositoryGitCredentialID,
    RepositoryAuthorizationType: values.RepositoryAuthorizationType,
    NewCredentialName: values.NewCredentialName,
    SaveCredential: values.SaveCredential,
    DeployMethod: 'helm',
  };

  const { isValid: isRepoValid, isChecking } = useGitRepoValidity({
    url: values.RepositoryURL || '',
    creds: getAuthentication(gitModel),
    tlsSkipVerify: values.TLSSkipVerify,
    enabled: isBE,
  });

  // Use Helm values preview hook (for merging multiple values files)
  const helmValuesPreviewQuery = useHelmValuesPreview({
    repository: values.RepositoryURL || '',
    valuesFiles: values.HelmValuesFiles || [],
    reference: values.RepositoryReferenceName,
    username: values.RepositoryUsername,
    password: values.RepositoryPassword,
    authorizationType: values.RepositoryAuthorizationType,
    gitCredentialID: values.RepositoryGitCredentialID,
    tlsSkipVerify: values.TLSSkipVerify,
    enabled:
      isRepoValid &&
      !!values.RepositoryURL &&
      Array.isArray(values.HelmValuesFiles) &&
      values.HelmValuesFiles.length > 0 &&
      values.HelmValuesFiles.every((f) => f.trim() !== ''),
  });

  // Validate that the chart path contains a valid Helm chart (Chart.yaml exists)
  const chartValidationQuery = useGitHelmChartValidation({
    repository: values.RepositoryURL || '',
    chartPath: values.HelmChartPath || './',
    reference: values.RepositoryReferenceName,
    username: values.RepositoryUsername,
    password: values.RepositoryPassword,
    authorizationType: values.RepositoryAuthorizationType,
    gitCredentialID: values.RepositoryGitCredentialID,
    tlsSkipVerify: values.TLSSkipVerify,
    enabled: isRepoValid,
  });

  const hasValuesErrors =
    isArrayErrorType<unknown>(errors.HelmValuesFiles) &&
    errors.HelmValuesFiles.some((error) => !!error);

  // Fetch the chart's default values.yaml from the chart root directory (matches helm show values behavior: https://github.com/helm/helm/blob/4877e2d7e99d61dddbc048414b86f114a2c06ca3/pkg/chart/v2/loader/load.go#L105-L110)
  const gitHelmChartValuesQuery = useGitHelmChartValues(
    {
      repository: values.RepositoryURL || '',
      chartPath: values.HelmChartPath || './',
      reference: values.RepositoryReferenceName,
      username: values.RepositoryUsername,
      password: values.RepositoryPassword,
      authorizationType: values.RepositoryAuthorizationType,
      gitCredentialID: values.RepositoryGitCredentialID,
      tlsSkipVerify: values.TLSSkipVerify,
      command: 'values',
    },
    !hasValuesErrors
  );

  // Fetch Chart.yaml to get chart metadata (version, name, etc.)
  const gitHelmChartInfoQuery = useGitHelmChartInfo({
    repository: values.RepositoryURL || '',
    chartPath: values.HelmChartPath || './',
    reference: values.RepositoryReferenceName,
    username: values.RepositoryUsername,
    password: values.RepositoryPassword,
    authorizationType: values.RepositoryAuthorizationType,
    gitCredentialID: values.RepositoryGitCredentialID,
    tlsSkipVerify: values.TLSSkipVerify,
    command: 'chart',
  });

  // Build preview payload for manifest preview with diff
  const previewPayload: GitHelmDryRunPayload = useMemo(
    () => ({
      name: release.name,
      namespace: release.namespace || '',
      repositoryURL: values.RepositoryURL || '',
      helmChartPath: values.HelmChartPath || './',
      helmValuesFiles: values.HelmValuesFiles || [],
      repositoryReferenceName: values.RepositoryReferenceName,
      repositoryAuthentication: values.RepositoryAuthentication,
      repositoryUsername: values.RepositoryUsername,
      repositoryPassword: values.RepositoryPassword,
      repositoryAuthorizationType: values.RepositoryAuthorizationType,
      repositoryGitCredentialID: values.RepositoryGitCredentialID,
      tlsSkipVerify: values.TLSSkipVerify,
    }),
    [values, release]
  );

  return (
    <div className="flex flex-col gap-8">
      <FormSection
        title={
          <span className="flex items-center gap-2">
            <span>Git repository</span>
            {isRepoValid && (
              <Badge type="success">
                {buildValidBadgeDetails(
                  values.RepositoryURL,
                  values.RepositoryReferenceName || ''
                )}
              </Badge>
            )}
            {!!values.RepositoryURL && isRepoValid === false && !isChecking && (
              <Badge type="danger">Invalid</Badge>
            )}
          </span>
        }
        isFoldable={!!initialValues.RepositoryURL}
        defaultFolded={!!initialValues.RepositoryURL}
        id="git-repository"
      >
        <TextTip color="blue" className="mb-3">
          Configure the Git repository where the Helm chart and Helm
          user-defined values files are located.
        </TextTip>
        <AuthFieldset
          value={gitModel}
          onChange={handleGitFormChange}
          isAuthExplanationVisible
          errors={errors}
        />
        <GitFormUrlField
          value={values.RepositoryURL}
          onChange={(value) => setFieldValue('RepositoryURL', value)}
          onChangeRepositoryValid={(value) =>
            setFieldValue('RepositoryURLValid', value)
          }
          model={gitModel}
          errors={
            isErrorType(errors.RepositoryURL) ? errors.RepositoryURL : undefined
          }
          placeholder="e.g. https://github.com/my-org/helm-charts"
        />
        <div className="form-group">
          <div className="col-sm-12">
            <SwitchField
              label="Skip TLS Verification"
              checked={values.TLSSkipVerify || false}
              onChange={(value) => setFieldValue('TLSSkipVerify', value)}
              name="TLSSkipVerify"
              tooltip="Enabling this will allow skipping TLS validation for any self-signed certificate."
              labelClass="col-sm-3 col-lg-2"
              data-cy="helm-git-tls-skip-verify"
            />
          </div>
        </div>
        <RefField
          value={values.RepositoryReferenceName || ''}
          onChange={(value) => setFieldValue('RepositoryReferenceName', value)}
          model={gitModel}
          error={
            isErrorType(errors.RepositoryReferenceName)
              ? errors.RepositoryReferenceName
              : undefined
          }
          isUrlValid={values.RepositoryURLValid}
        />
      </FormSection>

      {values.AutoUpdate && (
        <FormSection
          id="automatic-updates"
          title={
            <span className="flex items-center gap-2">
              <span>Automatic updates</span>
              {values.AutoUpdate.RepositoryAutomaticUpdates ? (
                <Badge type="infoSecondary">
                  {values.AutoUpdate.RepositoryMechanism === 'Interval'
                    ? `Polling${
                        values.AutoUpdate.RepositoryFetchInterval &&
                        ` every ${values.AutoUpdate.RepositoryFetchInterval}`
                      }`
                    : 'Webhook'}
                </Badge>
              ) : (
                <Badge type="muted">Disabled</Badge>
              )}
            </span>
          }
          isFoldable={!!initialValues.RepositoryURL}
          defaultFolded={!!initialValues.RepositoryURL}
        >
          <AutoUpdateFieldset
            environmentType="KUBERNETES"
            deployMethod="helm"
            isForcePullVisible={false}
            value={values.AutoUpdate}
            onChange={(autoUpdateValue) =>
              setFieldValue('AutoUpdate', autoUpdateValue)
            }
            errors={errors.AutoUpdate as FormikErrors<AutoUpdateModel>}
            baseWebhookUrl={baseStackWebhookUrl()}
            webhookId={values.webhookId || ''}
            webhooksDocs="/user/kubernetes/applications/webhooks"
          />
        </FormSection>
      )}

      {isRepoValid && (
        <>
          <FormSection title="Helm chart">
            <TextTip color="blue" className="mb-3">
              Set the path to the Helm chart folder. Only the Git repository can
              be used as a Helm chart source for now. To use an OCI registry or
              Helm repository, use Helm without Git.
            </TextTip>
            <FormControl
              label="Helm chart path"
              errors={
                errors.HelmChartPath ||
                (chartValidationQuery.isError
                  ? 'Invalid Helm chart path. Chart.yaml not found in this directory.'
                  : undefined)
              }
              tooltip="Indicate the path to the Helm chart folder from the root of your repository. The folder must contain a Chart.yaml file."
              className="relative z-[100]"
            >
              <PathSelector
                value={values.HelmChartPath || ''}
                onChange={(value) => setFieldValue('HelmChartPath', value)}
                placeholder="e.g. charts/my-app"
                model={gitModel}
                dirOnly
                allowEmpty
                inputId="helm-chart-path"
                hasError={
                  !!errors.HelmChartPath || chartValidationQuery.isError
                }
              />
              <TextTip color="blue" className="mb-3 mt-3">
                If no path is provided, the root folder of the repository will
                be used.
              </TextTip>
            </FormControl>
          </FormSection>
          <FormSection title="Upgrade options">
            {gitHelmChartInfoQuery.data?.version && (
              <FormControl
                label="Version"
                tooltip="The version of the Helm chart from Chart.yaml. This cannot be changed when deploying from Git."
              >
                <PortainerSelect
                  value={gitHelmChartInfoQuery.data.version}
                  options={[
                    {
                      label: gitHelmChartInfoQuery.data.version,
                      value: gitHelmChartInfoQuery.data.version,
                    },
                  ]}
                  disabled
                  onChange={() => {}}
                  data-cy="helm-chart-version-select"
                />
              </FormControl>
            )}
            <FormControl
              label="Rollback on failure"
              tooltip="Enables automatic rollback on failure. It may increase the time to upgrade."
              inputId="atomic-input"
            >
              <Checkbox
                id="atomic-input"
                checked={values.atomic}
                data-cy="atomic-checkbox"
                onChange={(e) => setFieldValue('atomic', e.target.checked)}
              />
            </FormControl>
            <FormControl
              label="Values files"
              tooltip={
                <>
                  <p>
                    Specify user-defined values files to override the default
                    chart values.
                  </p>
                  <p>
                    If multiple values files are specified, they will be merged
                    in the order they are specified (last values file takes
                    precedence).
                  </p>
                  <p>
                    You can get more information about the Helm values file
                    format in the{' '}
                    <ExternalLink
                      to="https://helm.sh/docs/chart_template_guide/values_files/"
                      data-cy="helm-values-file-format-link"
                    >
                      official documentation
                    </ExternalLink>
                    .
                  </p>
                </>
              }
            >
              <FieldArray name="HelmValuesFiles">
                {({ push, remove }) => (
                  <div>
                    {(values.HelmValuesFiles || []).map((file, index) => (
                      <div
                        key={`values-file-${index}`}
                        className="relative z-[90] mb-2"
                        style={{ zIndex: 90 - index }}
                      >
                        <div className="flex items-start gap-2 align-top">
                          <div className="flex-1">
                            <PathSelector
                              value={file}
                              onChange={(value) =>
                                setFieldValue(`HelmValuesFiles.${index}`, value)
                              }
                              placeholder="e.g. values/production.yaml"
                              model={gitModel}
                              dirOnly={false}
                              inputId={`helm-values-file-${index}`}
                              hasError={
                                isArrayErrorType<unknown>(
                                  errors.HelmValuesFiles
                                ) && !!errors.HelmValuesFiles?.[index]
                              }
                            />
                          </div>
                          <Button
                            type="button"
                            color="dangerlight"
                            className="h-[34px]"
                            data-cy={`helm-values-file-remove-${index}`}
                            onClick={() => remove(index)}
                            icon={Trash2}
                            size="medium"
                          >
                            <span className="sr-only">Remove</span>
                          </Button>
                        </div>
                        {isArrayErrorType<unknown>(errors.HelmValuesFiles) &&
                          errors.HelmValuesFiles?.[index] && (
                            <FormError>
                              {
                                errors.HelmValuesFiles?.[
                                  index
                                ] as unknown as string
                              }
                            </FormError>
                          )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      color="secondary"
                      size="small"
                      onClick={() => push('')}
                      data-cy="helm-values-file-add"
                      icon={Plus}
                      className="!ml-0"
                    >
                      Add values file
                    </Button>
                  </div>
                )}
              </FieldArray>
            </FormControl>
            {gitHelmChartValuesQuery.isError && (
              <TextTip color="orange" className="mb-3">
                Unable to load chart default values. The chart may not have a
                values.yaml file, or the chart path may be incorrect.
              </TextTip>
            )}
            {(helmValuesPreviewQuery.isError || hasValuesErrors) && (
              <TextTip color="orange" className="mb-3">
                Unable to load user-defined values from the specified files.
                Please verify the file paths are correct and the files contain
                valid YAML.
              </TextTip>
            )}
            <HelmValuesInput
              values={helmValuesPreviewQuery.data?.mergedValues ?? ''}
              setValues={() => {}} // No-op, read-only
              valuesRef={gitHelmChartValuesQuery.data?.values ?? ''}
              isValuesRefLoading={gitHelmChartValuesQuery.isInitialLoading}
              isUserDefinedLoading={helmValuesPreviewQuery.isInitialLoading}
              isUserDefinedReadOnly
              userDefinedLabel="User-defined values from Git (read-only)"
              userDefinedTooltip="These are the merged values from the values files specified in your Git repository. They are read-only and will be used during upgrade."
              userDefinedPlaceholder={`No user-defined values found${
                helmValuesPreviewQuery.isError
                  ? ' - check your values file paths and try again'
                  : ''
              }`}
            />
          </FormSection>
          <div className="-mt-8 mb-8">
            <ManifestPreviewFormSection
              mode="git"
              payload={previewPayload}
              onChangePreviewValidation={setPreviewIsValid}
              currentManifest={release.manifest}
              title="Manifest preview"
              environmentId={environmentId}
            />
          </div>
        </>
      )}
    </div>
  );

  function handleGitFormChange(partialGitValues: Partial<GitFormModel>) {
    // Update the parent form with the Git form changes
    Object.entries(partialGitValues).forEach(([key, value]) => {
      setFieldValue(key, value);
    });
  }
}

function buildValidBadgeDetails(repositoryUrl: string, referenceName: string) {
  const path = getRepositoryPathWithoutHost(repositoryUrl);
  const ref = referenceName.trim();

  if (path && ref) {
    return `Valid - ${path}, ${ref}`;
  }

  if (path) {
    return `Valid - ${path}`;
  }

  if (ref) {
    return `Valid - ${ref}`;
  }

  return 'Valid';
}
